import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Subject, take, takeUntil, zip } from 'rxjs';
import { AuthService } from './services/auth.service';

import { EmployerService } from './services/employer.service';

import { BootstrapService } from './services/bootstrap.service';
import { TranslateService } from '@ngx-translate/core';
import { ExaGrant } from './model/exa-grant';
import { EmployerDto, UserGrantDto } from './api/exa';
import { ModalController, Platform } from '@ionic/angular';
import { SettingsDialogComponent } from './components/settings-dialog/settings-dialog.component';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { StorageService } from './services/storage.service';
import { EmployerAddDialogComponent } from './components/employer-add-dialog/employer-add-dialog.component';
import { AlertService } from './services/alert.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  // Hide or show the Menu, see routing
  selected_employer = '';
  hideSplitPane = true;
  username = '';

  hasJobReadGrant = false;
  hasCustomerReadGrant = false;
  hasEmployeeReadGrant = false;
  hasMachineReadGrant = false;
  hasImplementReadGrant = false;
  hasFieldReadGrant = false;
  hasLocationReadGrant = false;

  employerDto: EmployerDto = null;
  userGrantDto: UserGrantDto = null;

  isDriver = false; // in case the user is driver the menu links shall be disabled regardless of the Read Grants

  lastEmployerSelected: string;

  private terminate$: Subject<void> = new Subject();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private log: NGXLogger,
    private auth: AuthService,
    private translate: TranslateService,
    private employerService: EmployerService,
    private exaBootstrapService: BootstrapService,
    private modalCtrl: ModalController,
    private platform: Platform,
    private storage: StorageService,
    private alertService: AlertService
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const val = this.activatedRoute.firstChild.snapshot.data.hideSplitPane;
        this.hideSplitPane = typeof val === 'undefined' ? false : val;
        const path = this.activatedRoute.firstChild.snapshot.routeConfig.path;
        this.log.debug(`hideSplitPane for page '${path}' is '${this.hideSplitPane}'`);
      }
    });

    this.initializeApp();

    this.clearAppDataOnFreshInstall();
  }

  

   ngOnInit(): void {
    this.platform.ready().then(() => {
      this.keepAwake();
    });

    let modalShown = false;    
    this.employerService.onGetEmployers().pipe().subscribe((employers) => {
      if (employers.length > 0) {
        this.auth.onAuthenticate().pipe().subscribe((user) => {          
          if (user) {
            this.userGrantDto = user;
            this.log.log('User DTO: ', user);
            this.username = `${user.firstName ? user.firstName + ' ' : ''}${user.lastName ? user.lastName + ' ' : ''}(${user.username})`;
          }
        });

        this.storage.get('lastEmployerSelected').then((val) => {
          if (val) {
            this.employerService.setSelectedEmployer(val);
            this.storage.set('lastEmployerSelected', val);
            const employerId = employers.find((e) => e.username === val)?.id;
            console.log('Employer DTO: ', employers.find((e) => e.username === val));
            this.updateGrants(employerId);
            this.checkIfUserIsDriver();
          } else {
            this.employerService.setSelectedEmployer(employers[0]?.username);
            this.storage.set('lastEmployerSelected', employers[0]?.username);
            const employerId = employers[0]?.id;
            console.log('Employer DTO: ', employers[0]);
            this.updateGrants(employerId);
            this.checkIfUserIsDriver();
            this.selectEmployer(modalShown);
          }
        });
      }
      else {
        this.selectEmployer(modalShown);
      }
    });
  }

  async clearAppDataOnFreshInstall() {
    const hasRun = localStorage.getItem('hasRun');

    if (!hasRun) {
      // First time run: clear storage
      localStorage.clear();
      sessionStorage.clear();
      this.storage.clear();

      // Mark that app has been run once
      localStorage.setItem('hasRun', 'true');
    }
  }

  private selectEmployer(modalShown: boolean): void {
    zip(this.employerService.onSelectEmployer())
      .pipe(takeUntil(this.terminate$))
      .subscribe((data) => {                
        this.employerDto = data[0];
        if (this.employerDto) {
          this.storage.set('lastEmployerSelected', this.employerDto?.username);
          const employerId = this.employerDto.id;
          console.log('Employer DTO: ', this.employerDto);

          this.updateGrants(employerId);
          this.checkIfUserIsDriver();
        } 
        // else if (!modalShown) {
        //   modalShown = true;
        //   setTimeout(() => {
        //     if (this.userGrantDto && !this.employerDto) {
        //       this.alertService.openAlertToast(this.translate.instant('ENTERPRISE.ADD.EMPLOYER_REQUIRED') + '🤷‍♂️', 4000);
        //       this.openAddEnterprise();
        //     }
        //   }, 5000);
        // }
      });
  }

  // Método para actualizar los permisos de lectura
  private updateGrants(employerId: number): void {
    this.hasJobReadGrant = this.auth.hasGrantOwner(ExaGrant.READ_JOB, employerId);
    this.hasCustomerReadGrant = this.auth.hasGrantOwner(ExaGrant.READ_USER_CUSTOMER, employerId);
    this.hasEmployeeReadGrant = this.auth.hasGrantOwner(ExaGrant.READ_EMPLOYEE, employerId);
    this.hasMachineReadGrant = this.auth.hasGrantOwner(ExaGrant.READ_MACHINE, employerId);
    this.hasImplementReadGrant = this.auth.hasGrantOwner(ExaGrant.READ_IMPLEMENT, employerId);
    this.hasFieldReadGrant = this.auth.hasGrantOwner(ExaGrant.READ_FIELD, employerId);
    this.hasLocationReadGrant = this.auth.hasGrantOwner(ExaGrant.READ_LOCATION, employerId);
  }

  async keepAwake() {
    await KeepAwake.keepAwake();
  }

  async allowSleep() {
    await KeepAwake.allowSleep();
  }

  initializeApp() {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: Style.Default });

      SplashScreen.hide();
    }
  }

  checkIfUserIsDriver(): void {
    // drivers are not allowed to enter all submenus regardless of their reading grants
    if (this.employerDto && this.userGrantDto) {
      // Driver Grant = zB 'DRIV-19' bei employerId 19
      const employerId = this.employerDto.id; // 19
      const driverGrantName = ExaGrant.ENTERPRISE_DRIVER + '-' + employerId;
      // console.log(" searching for grant: ", driverGrantName);
      if (this.userGrantDto.fineGrainedGrants.indexOf(driverGrantName) !== -1) {
        this.isDriver = true;
      } else {
        this.isDriver = false;
      }
    }
  }

  async openSettings() {
    const modal = await this.modalCtrl.create({
      component: SettingsDialogComponent,
      cssClass: 'custom-dialog h-500',
    });
    return await modal.present();
  }

  async openAddEnterprise() {
    const modal = await this.modalCtrl.create({
      component: EmployerAddDialogComponent,
      cssClass: 'custom-dialog h-300',
    });
    return await modal.present();
  }

  ngOnDestroy(): void {
    this.terminate$.next();
    this.terminate$.complete();
    localStorage.removeItem('mapLat');
    localStorage.removeItem('mapLng');
    localStorage.removeItem('mapZoom');
    localStorage.removeItem('bounds');
    localStorage.removeItem('bounds2');
    this.allowSleep();
  }
  
  public onClickLogout() {
    this.auth.logout();
  }
}
export class MenuSection {}
